"use client"

import { useEffect, useRef, useState } from "react"

import { useSearchParams } from "next/navigation"
import { useTranslations } from "next-intl"

import { BottomDock, DockRow } from "@/components/marketplace/layout/bottom-dock"
import { BottomNav } from "@/components/marketplace/layout/bottom-nav"
import { CategorySheet } from "@/components/marketplace/layout/category-sheet"
import { MobileSearchSheet } from "@/components/marketplace/layout/mobile-search-sheet"
import { useMobileSearchState } from "@/components/marketplace/layout/mobile-search-state"
import type { NavCategory } from "@/components/marketplace/types"
import { CloseIcon, IconButton, NavSearchIcon } from "@/components/ui"
import { usePathname, useRouter } from "@/i18n/navigation"
import { useRecentSearches } from "@/lib/hooks/search/use-recent-searches"
import { useScrollUpReveal } from "@/lib/hooks/use-scroll-up-reveal"
import { buildRoute, ROUTES, segmentsAfterRegion } from "@/lib/routes"
import { SEARCH_PARAM_KEYS } from "@/lib/types/search"
import { useInNativeHost } from "@/platform/bridge/use-native-bridge"
import { createConversionId } from "@/platform/observability/analytics/conversions"
import { trackSearch } from "@/platform/observability/analytics/v2/search"

const DISC = "size-fab press-glyph shrink-0 rounded-full"
const CLOSE_DISC = `${DISC} shadow-raised bg-surface-inverse text-fg-on-inverse hover:bg-surface-inverse/88`

export const MobileChrome = ({ categories = [] }: { categories?: NavCategory[] }) => {
  const t = useTranslations("nav")
  const router = useRouter()
  const pathname = usePathname()
  const activeQuery = useSearchParams().get(SEARCH_PARAM_KEYS.Q) ?? ""
  const { open: searchOpen, setOpen: setSearchOpen } = useMobileSearchState()
  const [categoriesOpen, setCategoriesOpen] = useState(false)
  const [navFocused, setNavFocused] = useState(false)
  const [searchText, setSearchText] = useState(activeQuery)
  const searchTriggerRef = useRef<HTMLButtonElement>(null)
  const restoreSearchFocus = useRef(false)

  const close = () => {
    restoreSearchFocus.current = searchOpen
    setSearchOpen(false)
    setCategoriesOpen(false)
  }
  useEffect(() => {
    if (searchOpen || !restoreSearchFocus.current) return
    restoreSearchFocus.current = false
    searchTriggerRef.current?.focus()
  }, [searchOpen])
  useEffect(() => {
    setSearchOpen(false)
    setCategoriesOpen(false)
    setSearchText(activeQuery)
  }, [pathname, activeQuery, setSearchOpen])

  const { add: addRecent } = useRecentSearches()
  const openSearch = () => {
    setCategoriesOpen(false)
    setSearchOpen(true)
  }
  const goToResults = (term: string) => {
    close()
    if (term.length === 0) return
    trackSearch({ search_term: term, conversionId: createConversionId() })
    addRecent(term)
    router.push(buildRoute.search({ text: term }))
  }
  const quiet = searchOpen || categoriesOpen
  const onProductPage = segmentsAfterRegion(pathname)[0] === ROUTES.PRODUCTS
  const navRevealed = useScrollUpReveal(onProductPage, pathname)
  const inNativeHost = useInNativeHost()
  const navHidden = !inNativeHost && onProductPage && !navRevealed && !quiet && !navFocused

  return (
    <>
      {categoriesOpen && !searchOpen && <CategorySheet categories={categories} onDismiss={close} />}
      <BottomDock quiet={quiet} navHidden={navHidden} raised={inNativeHost}>
        {searchOpen ? (
          <MobileSearchSheet value={searchText} onValueChange={setSearchText} onSubmitTerm={goToResults} onClose={close} />
        ) : inNativeHost ? null : (
          <DockRow data-bottom-nav-row="" onFocus={() => setNavFocused(true)} onBlur={(event) => setNavFocused(event.currentTarget.contains(event.relatedTarget as Node | null))}>
            <BottomNav onCategoriesPress={() => setCategoriesOpen((open) => !open)} categoriesOpen={categoriesOpen} />
            {categoriesOpen ? (
              <IconButton aria-label={t("closeMenu")} variant="ghost" icon={<CloseIcon size={24} />} onClick={close} className={CLOSE_DISC} />
            ) : (
              <IconButton
                ref={searchTriggerRef}
                aria-label={t("openSearch")}
                variant="ghost"
                icon={<NavSearchIcon />}
                onClick={openSearch}
                className={`${DISC} bg-brand text-fg-inverted hover:bg-brand-hover active:bg-brand-pressed md:hidden`}
              />
            )}
          </DockRow>
        )}
      </BottomDock>
    </>
  )
}
